import { useState } from 'react';
import { Alert, App, Button, Card, Col, Input, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';
import { downloadText } from '../../lib/csv';
import { dayRange } from '../dashboard/dashboardLinks';
import { RANGE_PRESETS, type StatsRangeParams } from '../listeners/listenerStats';
import { StatsRangeFilter, useUrlStatsRange } from '../listeners/ListenerStatsWidgets';
import { OnlineExportButton } from '../listeners/OnlineExportButton';
import {
  PREVIEW_ROWS,
  REPORTS,
  REPORT_FILTERS,
  REPORT_MAX_ROWS,
  previewOf,
  type ReportDef,
} from './reports';

function saveBlob(data: BlobPart, filename: string) {
  const url = URL.createObjectURL(new Blob([data], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Indian financial year containing today, e.g. "2026-27". */
function currentFy(): string {
  const d = new Date();
  const start = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`;
}

/** One report for the selected period: optional filter, preview, download. */
function ReportCard({ def, range, periodLabel }: { def: ReportDef; range: StatsRangeParams | null; periodLabel: string }) {
  const { message } = App.useApp();
  const [filter, setFilter] = useState<string | undefined>();
  const [busy, setBusy] = useState<'preview' | 'download' | null>(null);
  const [preview, setPreview] = useState<{ header: string[]; rows: string[][]; more: boolean } | null>(null);

  async function run(kind: 'preview' | 'download') {
    if (!range) return;
    setBusy(kind);
    try {
      const built = await def.build(range, { maxRows: kind === 'preview' ? PREVIEW_ROWS : REPORT_MAX_ROWS, filter });
      if (kind === 'preview') {
        setPreview({ ...previewOf(built.csv), more: built.truncated });
        return;
      }
      const days = dayRange(range);
      downloadText(`${def.key}_${days.from}_to_${days.to}.csv`, built.csv);
      if (built.truncated) {
        message.warning(`Exported the first ${REPORT_MAX_ROWS.toLocaleString('en-IN')} rows — narrow the period for the rest.`);
      } else {
        message.success(`${def.title}: ${built.rows.toLocaleString('en-IN')} rows exported`);
      }
    } catch {
      message.error(`Could not build the ${def.title} report`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card title={def.title} extra={<Tag>{periodLabel}</Tag>} style={{ height: '100%' }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Typography.Text type="secondary">{def.description}</Typography.Text>
        <Space wrap>
          {def.filter && (
            <Select
              allowClear
              aria-label={`${def.title} filter`}
              placeholder="All"
              style={{ minWidth: 200 }}
              value={filter}
              onChange={(v: string | undefined) => {
                setFilter(v);
                setPreview(null);
              }}
              options={REPORT_FILTERS[def.filter]}
            />
          )}
          <Button
            icon={<EyeOutlined />}
            disabled={!range}
            loading={busy === 'preview'}
            onClick={() => void run('preview')}
            aria-label={`Preview ${def.title}`}
          >
            Preview
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            disabled={!range}
            loading={busy === 'download'}
            onClick={() => void run('download')}
            aria-label={`Download ${def.title}`}
          >
            Download CSV
          </Button>
        </Space>
        {preview && (
          <Table
            size="small"
            rowKey={(_, i) => String(i)}
            dataSource={preview.rows}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No rows in this period' }}
            footer={() =>
              preview.more
                ? `First ${PREVIEW_ROWS} rows — download for all of them`
                : `${preview.rows.length} row${preview.rows.length === 1 ? '' : 's'}`
            }
            columns={preview.header.map((h, i) => ({ title: h, render: (_: unknown, row: string[]) => row[i] ?? '' }))}
          />
        )}
      </Space>
    </Card>
  );
}

/**
 * Reports — every export in one place, for one chosen period. Each report is
 * built from the same data its page shows and needs that page's permission.
 */
export function ReportsPage() {
  const { message } = App.useApp();
  const { can } = usePermissions();
  const { preset, setPreset, setCustom, range } = useUrlStatsRange('last7');
  const periodLabel = RANGE_PRESETS.find((p) => p.value === preset)?.label ?? 'Selected period';
  const [userQuery, setUserQuery] = useState('');
  const [fy, setFy] = useState(currentFy());
  const [busy, setBusy] = useState<'users' | 'tds' | null>(null);

  const reports = REPORTS.filter((r) => can(r.permission));

  async function download(kind: 'users' | 'tds') {
    setBusy(kind);
    try {
      if (kind === 'users') {
        const res = await api.get('/admin/users/export', { params: { q: userQuery.trim() || undefined }, responseType: 'blob' });
        saveBlob(res.data as BlobPart, `users-${new Date().toISOString().slice(0, 10)}.csv`);
      } else {
        const res = await api.get('/admin/reports/tds', { params: { fy }, responseType: 'blob' });
        saveBlob(res.data as BlobPart, `tds-${fy}.csv`);
      }
    } catch {
      message.error(kind === 'users' ? 'Could not download the user export' : 'Could not download the TDS report');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
            Reports
          </Typography.Title>
          <Typography.Text type="secondary">
            Pick a period, preview a report, download it as CSV. Listeners are always named by their display name.
          </Typography.Text>
        </div>
        <StatsRangeFilter preset={preset} setPreset={setPreset} setCustom={setCustom} />
      </div>
      {range === null && <Alert type="info" showIcon message="Pick both dates of the custom range." />}

      {can('reports.full_export') && (
        <Alert
          type="info"
          showIcon
          message="Need everything in one file?"
          description="The Full Data Report is one Excel workbook with every area of the panel — users, listeners, calls, wallets, earnings, payouts, referrals and settings."
          action={
            <Link to="/reports/full">
              <Button type="primary">Open Full Data Report</Button>
            </Link>
          }
        />
      )}

      <Row gutter={[16, 16]}>
        {reports.map((def) => (
          <Col xs={24} xl={12} key={def.key}>
            <ReportCard def={def} range={range} periodLabel={periodLabel} />
          </Col>
        ))}

        {can('listeners.activity.view') && (
          <Col xs={24} xl={12}>
            <Card title="Listener online time (day-wise, Excel)" style={{ height: '100%' }}>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Typography.Text type="secondary">
                  A summary sheet plus one sheet per approved listener with their daily online time. Has its own date
                  range.
                </Typography.Text>
                <OnlineExportButton filter={{ status: 'approved' }} scopeLabel="All listeners" searched={false} />
              </Space>
            </Card>
          </Col>
        )}

        {can('users.export') && (
          <Col xs={24} xl={12}>
            <Card title="Users" extra={<Tag>all time</Tag>} style={{ height: '100%' }}>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Typography.Text type="secondary">
                  The user table with full phone numbers. Not tied to the period; optionally only users matching a
                  search. Every download is audited.
                </Typography.Text>
                <Space wrap>
                  <Input
                    allowClear
                    aria-label="Users report search"
                    placeholder="@handle or phone last 4 (optional)"
                    style={{ minWidth: 260 }}
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                  />
                  <Button type="primary" icon={<DownloadOutlined />} loading={busy === 'users'} onClick={() => void download('users')}>
                    Download CSV
                  </Button>
                </Space>
              </Space>
            </Card>
          </Col>
        )}

        {can('payouts.reports.export') && (
          <Col xs={24} xl={12}>
            <Card title="Tax deducted (TDS)" extra={<Tag>financial year</Tag>} style={{ height: '100%' }}>
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Typography.Text type="secondary">
                  TDS withheld per listener for one financial year (gross and TDS, with PAN last 4).
                </Typography.Text>
                <Space wrap>
                  <Input
                    aria-label="Financial year"
                    placeholder="2026-27"
                    style={{ width: 140 }}
                    value={fy}
                    onChange={(e) => setFy(e.target.value)}
                  />
                  <Button type="primary" icon={<DownloadOutlined />} loading={busy === 'tds'} onClick={() => void download('tds')}>
                    Download CSV
                  </Button>
                </Space>
              </Space>
            </Card>
          </Col>
        )}
      </Row>
    </Space>
  );
}
